import "@/index.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import Dashboard from "@/pages/Dashboard";
import Reader from "@/pages/Reader";
import ReadingLog from "@/pages/ReadingLog";
import Upload from "@/pages/Upload";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Settings from "@/pages/Settings";
import Leaderboard from "@/pages/Leaderboard";
import Review from "@/pages/Review";
import Collections from "@/pages/Collections";
import MonthlySummary from "@/pages/MonthlySummary";
import { BookOpen, LayoutDashboard, Upload as UploadIcon, Library, LogOut, User, Settings as SettingsIcon, Moon, Sun, Trophy, Brain, FolderOpen, CalendarDays, WifiOff, Download } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useServiceWorker } from "@/hooks/useServiceWorker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen bg-neo-bg flex items-center justify-center">
        <div className="neo-card text-center p-8">
          <div className="w-12 h-12 border-4 border-neo-yellow border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-heading font-bold text-lg">Loading...</p>
        </div>
      </div>
    );
  }
  if (user === false) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const isActive = (path) => location.pathname === path;

  if (!user || user === false) return null;

  const navItems = [
    { path: "/", label: "Dashboard", icon: LayoutDashboard },
    { path: "/upload", label: "Upload", icon: UploadIcon },
    { path: "/reading-log", label: "Log", icon: Library },
    { path: "/collections", label: "Folders", icon: FolderOpen },
    { path: "/leaderboard", label: "Ranks", icon: Trophy },
    { path: "/review", label: "Review", icon: Brain },
    { path: "/monthly-summary", label: "Summary", icon: CalendarDays },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white dark:bg-[#1E1E1E] border-b-[3px] border-neo-border dark:border-[#444]" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 group"
            data-testid="nav-logo"
          >
            <div className="w-10 h-10 rounded-lg bg-neo-yellow border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] dark:shadow-[2px_2px_0px_0px_#444] flex items-center justify-center group-hover:translate-x-[1px] group-hover:translate-y-[1px] group-hover:shadow-[1px_1px_0px_0px_#1A1A1A] transition-all">
              <BookOpen className="w-5 h-5 text-neo-text" />
            </div>
            <span className="font-heading font-extrabold text-xl tracking-tight text-neo-text dark:text-white hidden sm:block">
              PhD Reader
            </span>
          </button>
          <div className="flex items-center gap-1 md:gap-2 overflow-x-auto scrollbar-hidden flex-shrink min-w-0">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`neo-btn text-xs md:text-sm flex items-center gap-1 md:gap-2 flex-shrink-0 px-2 md:px-3 py-1.5 md:py-2 ${
                    isActive(item.path) ? "neo-btn-primary" : "bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white text-neo-text"
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden xl:inline">{item.label}</span>
                </button>
              );
            })}
            {/* Dark mode quick toggle */}
            <button
              onClick={toggleDarkMode}
              className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white p-2"
              data-testid="navbar-dark-mode-toggle"
            >
              {darkMode ? <Sun className="w-4 h-4 text-neo-yellow" /> : <Moon className="w-4 h-4" />}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white p-2" data-testid="user-menu-trigger">
                  <User className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-2 border-neo-border dark:border-[#444] shadow-[3px_3px_0px_0px_#1A1A1A] dark:shadow-[3px_3px_0px_0px_#444] dark:bg-[#1E1E1E]">
                <div className="px-3 py-2 border-b-2 border-neo-border/20 dark:border-[#444]/40">
                  <p className="font-heading font-bold text-sm dark:text-white">{user?.name || "User"}</p>
                  <p className="text-xs text-neo-text-secondary dark:text-gray-400">{user?.email}</p>
                </div>
                <DropdownMenuItem
                  onClick={() => navigate("/settings")}
                  className="cursor-pointer dark:text-white"
                  data-testid="settings-nav-btn"
                >
                  <SettingsIcon className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator className="dark:bg-[#444]" />
                <DropdownMenuItem
                  onClick={async () => { await logout(); navigate("/login"); }}
                  className="text-neo-red cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
}

function AppRouter() {
  const location = useLocation();
  const { isOffline, canInstall, promptInstall } = useServiceWorker();

  // CRITICAL: Detect session_id during render, NOT in useEffect
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      {/* Offline Banner */}
      {isOffline && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-neo-red text-white text-center py-2 px-4 text-sm font-bold flex items-center justify-center gap-2" data-testid="offline-banner">
          <WifiOff className="w-4 h-4" />
          You're offline — cached pages and PDFs are still available
        </div>
      )}
      {/* Install Prompt */}
      {canInstall && (
        <div className="fixed bottom-20 right-4 z-50" data-testid="install-prompt">
          <button
            onClick={promptInstall}
            className="neo-btn neo-btn-primary flex items-center gap-2 text-sm shadow-lg"
            data-testid="install-app-btn"
          >
            <Download className="w-4 h-4" />
            Install App
          </button>
        </div>
      )}
      <Navbar />
      <main className={`${isOffline ? "pt-28" : "pt-20"} pb-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto`}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<ProtectedRoute><Dashboard api={API} /></ProtectedRoute>} />
          <Route path="/upload" element={<ProtectedRoute><Upload api={API} /></ProtectedRoute>} />
          <Route path="/reader/:paperId" element={<ProtectedRoute><Reader api={API} /></ProtectedRoute>} />
          <Route path="/reading-log" element={<ProtectedRoute><ReadingLog api={API} /></ProtectedRoute>} />
          <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard api={API} /></ProtectedRoute>} />
          <Route path="/collections" element={<ProtectedRoute><Collections api={API} /></ProtectedRoute>} />
          <Route path="/review" element={<ProtectedRoute><Review api={API} /></ProtectedRoute>} />
          <Route path="/monthly-summary" element={<ProtectedRoute><MonthlySummary api={API} /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings api={API} /></ProtectedRoute>} />
        </Routes>
      </main>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <div className="min-h-screen bg-neo-bg dark:bg-[#141414] font-body transition-colors duration-200">
            <AppRouter />
            <Toaster position="top-right" />
          </div>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
